import { inject, Injectable } from '@angular/core';
import { ChartDatasetConfig, ChartDataService } from '@app/core/services/charts/chart-data.service';
import { ChartData, ChartOptions, TooltipOptions } from 'chart.js';
import { _, TranslateService } from '@ngx-translate/core';
import { splitTextByLines } from '@app/shared/utils/split-label-by-words';
import { roundToTwoDecimals } from '@app/shared/utils/math';
import { ColorManagementService } from '@app/core/services/colors/color-management.service';
import { FloatingPopupLineChartService } from '@app/main/main-content/floating-popup-manager/services/floating-popup-line-chart.service';
import { _DeepPartialObject } from 'node_modules/chart.js/dist/types/utils';
import { ILocalizationKey } from '@app/core/interfaces/localization';
import { ILoggerNoiseSpectrum } from '@app/api/interfaces/chart-data/logger-noise-spectrum';

@Injectable()
export class FpLoggerNoiseSpectrumChartService {
  protected floatingPopupLineChartService = inject(FloatingPopupLineChartService);
  protected chartDataService = inject(ChartDataService);
  protected translate: TranslateService = inject(TranslateService);
  protected colorManagementService = inject(ColorManagementService);

  private noDataLocalizationKey: ILocalizationKey = _(`@@shared-no-data-value-placeholder`);

  public generateOptions(data: ILoggerNoiseSpectrum[]): ChartOptions {
    const xValues = data.map(el => this.dataConverter(el).x);
    const maxXAxis = Math.max(...xValues);
    const minXAxis = Math.min(...xValues);

    const xLabel = _('@@shared-hz_label');

    const tooltipOptions: _DeepPartialObject<TooltipOptions> = this.getTooltipOptions();

    return this.floatingPopupLineChartService.generateOptions(
      { yLabel: null, xLabel, titleText: null },
      minXAxis,
      maxXAxis,
      true,
      tooltipOptions
    );
  }

  public generateMinimizedOptions(data: ILoggerNoiseSpectrum[]): ChartOptions {
    const xValues = data.map(el => this.dataConverter(el).x);
    const maxXAxis = Math.max(...xValues);
    const minXAxis = Math.min(...xValues);

    const xLabel = _('@@shared-hz_label');

    const tooltipOptions: _DeepPartialObject<TooltipOptions> = this.getTooltipOptions();

    return this.floatingPopupLineChartService.generateOptions(
      { yLabel: null, xLabel, titleText: null },
      minXAxis,
      maxXAxis,
      true,
      tooltipOptions,
      false
    );
  }

  private dataConverter(data: ILoggerNoiseSpectrum): { x: number; y: number } {
    return {
      y: roundToTwoDecimals(data.spectrumDbValue) || 0,
      x: roundToTwoDecimals(data.frequency) || 0,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private generateDatasetConfig(interactionsEnabled: boolean): ChartDatasetConfig<ILoggerNoiseSpectrum>[] {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const getMainColor = (item: ILoggerNoiseSpectrum, index: number) => {
      return this.colorManagementService.getChartDefaultColor();
    };

    return [
      {
        label: '',
        tooltipLabel: '',
        type: 'line',
        dataValue: (data: ILoggerNoiseSpectrum) => this.dataConverter(data),
        borderColor: (item, index) => getMainColor(item, index).toRgbString(),
        backgroundColor: (item, index) => {
          const color = getMainColor(item, index);
          return this.colorManagementService.getColorInstanceWithOpacity(color, 0.4).toRgbString();
        },
        borderWidth: 1,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 2,
        pointBackgroundColor: (item, index) => getMainColor(item, index).toRgbString(),
        pointBorderColor: (item, index) => getMainColor(item, index).toRgbString(),
        pointBorderWidth: 1,
      },
    ];
  }

  public getDataForChart(data: ILoggerNoiseSpectrum[]): ChartData {
    const datasetConfigs = this.generateDatasetConfig(true);
    return {
      datasets: this.chartDataService.generateDatasets<ILoggerNoiseSpectrum>([...data], datasetConfigs),
    };
  }

  public getMinimizedDataForChart(data: ILoggerNoiseSpectrum[]): ChartData {
    const datasetConfigs = this.generateDatasetConfig(false);
    return {
      datasets: this.chartDataService.generateDatasets<ILoggerNoiseSpectrum>([...data], datasetConfigs),
    };
  }

  private getTooltipOptions(): _DeepPartialObject<TooltipOptions> {
    return {
      callbacks: {
        title: () => {
          const text = this.translate.instant(_('@@shared-sound-signal-spectrum'));
          return splitTextByLines(text, 40);
        },
        label: tooltipItem => {
          const noDataText: string = this.translate.instant(this.noDataLocalizationKey);
          const { x, y } = tooltipItem.raw as { x: number; y: number };
          const xFormattedValue = this.floatingPopupLineChartService.formatChartNumber(x, 0, 2);
          const yFormattedValue = this.floatingPopupLineChartService.formatChartNumber(y, 0, 2);
          const separator = '   ';
          return this.translate.instant(_('@@chart-xy-x__hz'), {
            I18N_VAR_X_VALUE: xFormattedValue ?? noDataText,
            I18N_VAR_SEPARATOR: separator,
            I18N_VAR_Y_VALUE: yFormattedValue ?? noDataText,
          });
        },
      },
    };
  }
}
